import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./Overheads.css";
import {
  Wallet,
  Plus,
  Trash2,
  Save as SaveIcon,
  Info,
  Calculator,
} from "lucide-react";

export default function Overheads() {
  const { companyId } = useCompany();
  const [rows, setRows] = useState([]);

  // 1️⃣ Load existing overheads when companyId changes (UNCHANGED)
  useEffect(() => {
    if (!companyId) {
      setRows([]);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from("overheads")
        .select("id, sn, description, amount")
        .eq("company_id", companyId)
        .order("sn", { ascending: true });

      if (error) {
        console.error("Error loading overheads:", error);
        return;
      }

      if (data?.length) {
        setRows(data);
      } else {
        setRows([{ id: null, sn: 1, description: "", amount: "" }]);
      }
    })();
  }, [companyId]);

  // Add a new blank row (UNCHANGED)
  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { id: null, sn: prev.length + 1, description: "", amount: "" },
    ]);
  };

  // Update a field in a row (UNCHANGED)
  const handleInputChange = (idx, field, val) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: val } : r))
    );
  };

  // Delete a row and renumber (UNCHANGED)
  const deleteRow = async (idx) => {
    const row = rows[idx];
    if (row.id) {
      const { error } = await supabase
        .from("overheads")
        .delete()
        .eq("id", row.id)
        .eq("company_id", companyId);
      if (error) console.error("Error deleting overhead:", error);
    }
    setRows((prev) =>
      prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sn: i + 1 }))
    );
  };

  // Save/upsert all rows and remove stale ones (UNCHANGED)
  const saveData = async () => {
    if (!companyId) {
      alert("Select a company first.");
      return;
    }
    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();
    if (authErr || !user) {
      alert("Please login to save.");
      return;
    }
    const userId = user.id;

    try {
      const payload = rows.map((r) => ({
        id: r.id || uuidv4(),
        company_id: companyId,
        user_id: userId,
        sn: r.sn,
        description: r.description,
        amount: r.amount,
      }));

      let { error: upErr } = await supabase
        .from("overheads")
        .upsert(payload, { onConflict: ["user_id", "sn"] });
      if (upErr) throw upErr;

      const { data: existing, error: fetchErr } = await supabase
        .from("overheads")
        .select("id")
        .eq("company_id", companyId)
        .eq("user_id", userId);
      if (fetchErr) throw fetchErr;

      const keepIds = payload.map((r) => r.id);
      const removeIds = existing
        .map((r) => r.id)
        .filter((id) => !keepIds.includes(id));

      for (const id of removeIds) {
        const { error: delErr } = await supabase
          .from("overheads")
          .delete()
          .eq("id", id);
        if (delErr) console.error("Error deleting stale overhead:", delErr);
      }
      alert("Overheads saved successfully!");
    } catch (err) {
      console.error("Error saving overheads:", err);
      alert("Error saving overheads: " + err.message);
    }
  };

  // —— UI helpers (no logic changes) ——
  const monthlyTotal = useMemo(
    () => rows.reduce((sum, r) => sum + (parseFloat(r.amount || 0) || 0), 0),
    [rows]
  );

  const onRowKeyDown = (e, idx) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (idx === rows.length - 1) addRow();
    }
  };

  return (
    <div className="oh-shell">
      {/* Header */}
      <header className="panel oh-head">
        <div className="title">
          <h2>
            <Wallet size={18} /> Monthly Overheads
          </h2>
          <p className="muted">
            Capture operating costs spread across projects. These feed into
            pricing on your estimates & invoices.
          </p>
        </div>
        <div className="actions">
          <button className="btn-ghost" onClick={addRow}>
            <Plus size={16} /> Add Overhead
          </button>
          <button className="btn-primary" onClick={saveData}>
            <SaveIcon size={16} /> Save Overheads
          </button>
        </div>
      </header>

      {/* Totals */}
      <section className="panel oh-meter">
        <div className="meter-row">
          <span className="label">
            <Calculator size={14} /> Monthly Total
          </span>
          <span className="pill">
            ₦{Number.isFinite(monthlyTotal) ? monthlyTotal.toLocaleString() : 0}
          </span>
        </div>
        <p className="muted tiny">
          <Info size={12} /> Overheads are allocated proportionally in pricing
          models (see <strong>Invoice / Estimate</strong> tabs).
        </p>
      </section>

      {/* Table */}
      <section className="panel table-card">
        <div className="table-wrap">
          <table className="oh-table">
            <thead>
              <tr>
                <th style={{ width: 64 }}>S/N</th>
                <th>Description</th>
                <th style={{ width: 240 }}>Amount (₦) / Month</th>
                <th style={{ width: 110 }} className="center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i}>
                  <td>{r.sn}</td>
                  <td>
                    <input
                      className="oh-input"
                      type="text"
                      placeholder="e.g., Rent, Utilities, Internet, Insurance"
                      value={r.description}
                      onChange={(e) =>
                        handleInputChange(i, "description", e.target.value)
                      }
                      onKeyDown={(e) => onRowKeyDown(e, i)}
                    />
                  </td>
                  <td>
                    <div className="input-prefix">
                      <span className="prefix">₦</span>
                      <input
                        className="oh-input"
                        type="number"
                        inputMode="decimal"
                        placeholder="0"
                        value={r.amount}
                        onChange={(e) =>
                          handleInputChange(i, "amount", e.target.value)
                        }
                        onKeyDown={(e) => onRowKeyDown(e, i)}
                      />
                    </div>
                  </td>
                  <td className="center">
                    <button
                      type="button"
                      className="icon-btn danger"
                      onClick={() => deleteRow(i)}
                      aria-label={`Delete row ${r.sn}`}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan="4">
                    <div className="empty-state">
                      No overheads yet. Click <strong>Add Overhead</strong> to
                      begin.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer actions */}
        <div className="table-actions">
          <button className="btn-ghost" onClick={addRow}>
            <Plus size={16} /> Add Overhead
          </button>
          <button className="btn-primary" onClick={saveData}>
            <SaveIcon size={16} /> Save Overheads
          </button>
        </div>
      </section>
    </div>
  );
}
